import { ApiOutlined, CheckOutlined, FilterOutlined, CloseOutlined, LeftOutlined, LoadingOutlined, LogoutOutlined, SearchOutlined } from "@ant-design/icons";
import { Dropdown, Modal } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import ApiLogin from "./ApiLogin";

export type ApiAuthStatus = "checking" | "authenticated" | "unauthenticated";
export const API_DOCUMENT_URL = "https://sfer1z5wd8.execute-api.eu-west-1.amazonaws.com/staging/api/v1/swagger.json";
export const API_BASE_URL = "https://sfer1z5wd8.execute-api.eu-west-1.amazonaws.com/staging/api/v1";
const apiMethodClassName = (method: string) => ({ GET: "bg-sky-500 text-white", POST: "bg-emerald-500 text-white", PUT: "bg-amber-500 text-white", PATCH: "bg-violet-500 text-white", DELETE: "bg-rose-500 text-white" }[method] || "bg-slate-600 text-white");
const apiMethodFilterClassName = (method: string) => ({
    GET: "border-sky-400/50 bg-sky-500/20 text-sky-200 hover:bg-sky-500/30",
    POST: "border-emerald-400/50 bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30",
    PUT: "border-amber-400/50 bg-amber-500/20 text-amber-200 hover:bg-amber-500/30",
    PATCH: "border-violet-400/50 bg-violet-500/20 text-violet-200 hover:bg-violet-500/30",
    DELETE: "border-rose-400/50 bg-rose-500/20 text-rose-200 hover:bg-rose-500/30",
}[method] || "border-slate-400/50 bg-slate-500/20 text-slate-200 hover:bg-slate-500/30");
const apiRowClassName = (method: string) => ({ GET: "border-b-sky-500/70 bg-transparent [&_.api-row-outline]:border-sky-500/70 hover:bg-sky-500/15", POST: "border-b-emerald-500/70 bg-transparent [&_.api-row-outline]:border-emerald-500/70 hover:bg-emerald-500/15", PUT: "border-b-amber-500/70 bg-transparent [&_.api-row-outline]:border-amber-500/70 hover:bg-amber-500/15", PATCH: "border-b-violet-500/70 bg-transparent [&_.api-row-outline]:border-violet-500/70 hover:bg-violet-500/15", DELETE: "border-b-rose-500/70 bg-transparent [&_.api-row-outline]:border-rose-500/70 hover:bg-rose-500/15" }[method] || "border-b-slate-600 bg-transparent [&_.api-row-outline]:border-slate-600 hover:bg-slate-800/40");

const ApiSummary = ({ text }: { text: string }) => {
    const containerRef = useRef<HTMLSpanElement>(null);
    const textRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        const container = containerRef.current;
        const content = textRef.current;
        if (!container || !content) return;
        const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
        let animation: Animation | undefined;
        const updateAnimation = () => {
            animation?.cancel();
            const distance = content.scrollWidth - container.clientWidth;
            if (distance <= 0 || container.clientWidth === 0 || reducedMotion.matches) return;
            animation = content.animate([
                { transform: "translateX(0)", offset: 0 },
                { transform: "translateX(0)", offset: 0.15 },
                { transform: `translateX(-${distance}px)`, offset: 0.85 },
                { transform: `translateX(-${distance}px)`, offset: 1 },
            ], {
                duration: Math.max(3000, distance * 35 + 1200),
                direction: "alternate",
                iterations: Infinity,
                easing: "ease-in-out",
            });
        };
        const observer = new ResizeObserver(updateAnimation);
        observer.observe(container);
        observer.observe(content);
        reducedMotion.addEventListener("change", updateAnimation);
        updateAnimation();
        return () => {
            observer.disconnect();
            reducedMotion.removeEventListener("change", updateAnimation);
            animation?.cancel();
        };
    }, [text]);

    return (
        <span className="min-w-0 flex-1 overflow-hidden text-xs text-slate-400" ref={containerRef} title={text}>
            <span className="inline-block whitespace-nowrap align-middle" ref={textRef}>{text}</span>
        </span>
    );
};

interface ApiModalProps {
    authStatus: ApiAuthStatus;
    apiFormFields: any[];
    apiFormValues: Record<string, string>;
    apiListError: string;
    apiRequestError: string;
    apiSearch: string;
    endpointCount: number;
    filteredApiEndpoints: any[];
    handleApiLogin: () => void | Promise<void>;
    isCallingApi: boolean;
    isLoadingApis: boolean;
    isOpen: boolean;
    loadApis: () => void;
    openApiForm: (endpoint: any) => void;
    callSelectedApi: () => void;
    onClose: () => void;
    onLogout: () => void | Promise<void>;
    selectedApiEndpoint: any;
    setApiEndpoints: (value: any[]) => void;
    setApiFormValues: (updater: any) => void;
    setApiSearch: (value: string) => void;
    setSelectedApiEndpoint: (value: any) => void;
    title?: string;
    embedded?: boolean;
}

const ApiModal = ({ authStatus: apiAuthStatus, apiFormFields, apiFormValues, apiListError, apiRequestError, apiSearch, endpointCount, filteredApiEndpoints, handleApiLogin, isCallingApi, isLoadingApis, isOpen, loadApis, openApiForm, callSelectedApi, onClose, onLogout, selectedApiEndpoint, setApiEndpoints, setApiFormValues, setApiSearch, setSelectedApiEndpoint, title, embedded = false }: ApiModalProps) => {
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [selectedMethods, setSelectedMethods] = useState<string[]>([]);
    const apiTags = useMemo(() => Array.from(new Set<string>(filteredApiEndpoints.flatMap(endpoint => endpoint.tags || []))).sort(), [filteredApiEndpoints]);
    const apiMethods = useMemo(() => Array.from(new Set<string>(filteredApiEndpoints.map(endpoint => endpoint.method))).sort(), [filteredApiEndpoints]);
    const visibleApiEndpoints = useMemo(() => filteredApiEndpoints.filter(endpoint =>
        (!selectedTags.length || selectedTags.some(tag => endpoint.tags?.includes(tag)))
        && (!selectedMethods.length || selectedMethods.includes(endpoint.method))
    ), [filteredApiEndpoints, selectedMethods, selectedTags]);

    const filterCount = selectedMethods.length + selectedTags.length;
    const filterSections = [
        { label: "Method", options: apiMethods, selected: selectedMethods, setSelected: setSelectedMethods },
        { label: "Group", options: apiTags, selected: selectedTags, setSelected: setSelectedTags },
    ];
    const filterPanel = (
        <div aria-label="API filters" className="w-80 max-w-[calc(100vw-24px)] overflow-hidden rounded-lg border border-slate-700 bg-[#202329] shadow-xl shadow-black/40" role="dialog">
            <div className="max-h-[60dvh] overflow-y-auto p-3">
                {filterSections.map(section => (
                    <section className="border-b border-slate-700 pb-3 last:border-0 last:pb-0 [&+section]:pt-3" key={section.label}>
                        <div className="mb-2 flex items-center justify-between gap-3">
                            <h3 className="m-0 text-xs font-semibold text-slate-200">Filter by {section.label.toLowerCase()}</h3>
                            <button className="rounded px-1 py-0.5 text-xs text-indigo-300 transition hover:bg-indigo-500/10 disabled:cursor-default disabled:text-slate-600" disabled={!section.selected.length} onClick={() => section.setSelected([])} type="button">Clear all</button>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {section.options.map(option => (
                                <button aria-pressed={section.selected.includes(option)} className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-semibold transition-colors ${section.selected.includes(option) ? section.label === "Method" ? apiMethodFilterClassName(option) : "border-indigo-400/50 bg-indigo-500/20 text-indigo-200" : "border-slate-700 bg-slate-800/50 text-slate-400 hover:border-slate-500 hover:text-slate-200"}`} key={option} onClick={() => section.setSelected(values => values.includes(option) ? values.filter(value => value !== option) : [...values, option])} type="button">
                                    {section.selected.includes(option) && <CheckOutlined className="text-[10px]" />}{option}
                                </button>
                            ))}
                            {!section.options.length && <span className="text-xs text-slate-500">No options available.</span>}
                        </div>
                    </section>
                ))}
            </div>
        </div>
    );

    const apiSearchInput = (
        <div className={`${embedded ? "mx-auto h-8 w-full min-w-0 sm:max-w-md sm:flex-1" : "mb-3 h-10"} flex items-center gap-2 rounded-md border border-slate-700 bg-slate-950 pl-3 pr-0 text-slate-500 focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/15`}>
            <SearchOutlined className="shrink-0" />
            <input
                aria-label="Search APIs"
                autoFocus={!embedded}
                className="min-w-0 flex-1 bg-transparent text-sm text-slate-200 outline-none placeholder:text-slate-600"
                onChange={event => setApiSearch(event.target.value)}
                placeholder="Search API path..."
                value={apiSearch}
            />
            {apiSearch && (
                <button
                    aria-label="Clear API search"
                    className="flex h-6 w-6 items-center justify-center rounded text-xs hover:bg-slate-800 hover:text-slate-300"
                    onClick={() => setApiSearch("")}
                    type="button"
                >
                    <CloseOutlined />
                </button>
            )}
            <Dropdown placement="bottomRight" trigger={["click"]} popupRender={() => filterPanel}>
                <button aria-label="Filter APIs" className={`flex h-7 shrink-0 items-center justify-center gap-1.5 border-l border-slate-700 px-3 transition hover:text-indigo-300 ${filterCount ? "text-indigo-300" : "text-slate-400"}`} title="Filter APIs" type="button">
                    <FilterOutlined />
                    {filterCount > 0 && <span className="rounded bg-indigo-500/20 px-1 text-[10px] font-semibold">{filterCount}</span>}
                </button>
            </Dropdown>
        </div>
    );

    const content = (
        <>
            {apiAuthStatus === "checking" ? (
                <div role="status" className="flex min-h-80 flex-col items-center justify-center gap-3 text-sm text-slate-400">
                    <LoadingOutlined aria-hidden="true" className="animate-spin text-2xl text-indigo-400 motion-reduce:animate-none" />
                    <span>Checking login...</span>
                </div>
            ) : apiAuthStatus === "unauthenticated" ? (
                <ApiLogin onLogin={handleApiLogin} />
            ) : selectedApiEndpoint ? (
                <div className={embedded ? "sm:pr-1" : "max-h-[70vh] overflow-y-auto pr-1"}>
                    <button
                        className="mb-2 hidden h-8 items-center gap-1.5 rounded-md px-1 text-sm font-semibold text-slate-400 hover:bg-slate-800 hover:text-indigo-400 sm:mb-4 sm:flex sm:px-2"
                        onClick={() => setSelectedApiEndpoint(null)}
                        type="button"
                    >
                        <span className="flex h-5 w-5 items-center justify-center" aria-hidden="true"><LeftOutlined /></span>
                        Back to APIs
                    </button>

                    <div className="mb-3 px-0 py-1 sm:mb-5 sm:p-4">
                        <div className="flex items-center gap-3">
                            <span className={`w-16 shrink-0 rounded px-2 py-1 text-center font-mono text-[11px] font-bold ${apiMethodClassName(selectedApiEndpoint.method)}`}>
                                {selectedApiEndpoint.method}
                            </span>
                            <div className="min-w-0">
                                <div className="break-all font-mono text-sm font-semibold text-slate-100">{selectedApiEndpoint.path}</div>
                            </div>
                        </div>
                        {selectedApiEndpoint.description && (
                            <div className="mt-2 flex items-center gap-2 border-l-2 border-amber-500/60 py-1 pl-2 text-xs text-slate-400 sm:mt-3 sm:pl-3">
                                <span className="shrink-0 font-semibold text-amber-400">Required roles</span>
                                <span>{selectedApiEndpoint.description.replace(/[*`]/g, "").replace(/^Requires role:\s*/i, "")}</span>
                            </div>
                        )}
                    </div>

                    {apiFormFields.length ? (
                        <div className="space-y-3 px-1 sm:space-y-4 sm:px-0">
                            {apiFormFields.map(field => (
                                <label className="block max-sm:!pl-0" key={field.key}>
                                    <span className="mb-1 flex flex-wrap items-center gap-2 text-xs font-semibold text-slate-300 sm:mb-1.5">
                                        {field.label}
                                        {field.required && <span className="text-rose-400">*</span>}
                                        <span className="rounded bg-slate-800 px-1.5 py-0.5 font-normal uppercase text-slate-500">{field.location}</span>
                                        <span className="font-normal text-slate-600">{field.type}</span>
                                    </span>
                                    {field.type === "object" || field.type === "array" ? (
                                        <textarea
                                            className="min-h-24 w-full resize-y rounded-md border border-slate-700 bg-slate-950 px-3 py-2 font-mono text-sm text-slate-200 outline-none placeholder:text-slate-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/15"
                                            onChange={event => setApiFormValues((values: Record<string, string>) => ({ ...values, [field.key]: event.target.value }))}
                                            placeholder={field.type === "array" ? "[]" : "{}"}
                                            required={field.required}
                                            value={apiFormValues[field.key] || ""}
                                        />
                                    ) : field.type === "boolean" ? (
                                        <select
                                            className="h-10 w-full rounded-md border border-slate-700 bg-slate-950 px-3 text-sm text-slate-200 outline-none focus:border-indigo-500"
                                            onChange={event => setApiFormValues((values: Record<string, string>) => ({ ...values, [field.key]: event.target.value }))}
                                            required={field.required}
                                            value={apiFormValues[field.key] || ""}
                                        >
                                            <option value="">Select a value</option>
                                            <option value="true">true</option>
                                            <option value="false">false</option>
                                        </select>
                                    ) : (
                                        <input
                                            className="h-10 w-full rounded-md border border-slate-700 bg-slate-950 px-3 text-sm text-slate-200 outline-none placeholder:text-slate-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/15"
                                            onChange={event => setApiFormValues((values: Record<string, string>) => ({ ...values, [field.key]: event.target.value }))}
                                            required={field.required}
                                            type={field.type === "integer" || field.type === "number" ? "number" : "text"}
                                            value={apiFormValues[field.key] || ""}
                                        />
                                    )}
                                    {field.description && <span className="mt-1 block text-xs text-slate-500">{field.description}</span>}
                                </label>
                            ))}
                        </div>
                    ) : (
                        <div className="rounded-md border border-dashed border-slate-700 px-3 py-5 text-center text-sm text-slate-500 sm:px-4 sm:py-10">
                            This API has no input parameters.
                        </div>
                    )}
                    {apiRequestError && (
                        <div className="mt-3 rounded-md border border-rose-900/60 bg-rose-950/30 px-3 py-2 text-xs text-rose-400 sm:mt-4">
                            {apiRequestError}
                        </div>
                    )}
                    <div className="mt-3 flex justify-end gap-2 border-t border-slate-700 pt-3 sm:mt-5 sm:pt-4">
                        <button
                            className="flex h-9 items-center gap-2 rounded-md border border-slate-700 px-4 text-sm font-semibold text-slate-300 transition hover:border-slate-500 hover:bg-slate-700/50 sm:h-10"
                            onClick={() => setSelectedApiEndpoint(null)}
                            type="button"
                        >
                            <LeftOutlined />
                            Back to APIs
                        </button>
                        <button
                            className="h-9 rounded-md bg-indigo-600 px-5 text-sm font-bold text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 sm:h-10"
                            disabled={isCallingApi}
                            onClick={() => callSelectedApi()}
                            type="button"
                        >
                            {isCallingApi ? <><LoadingOutlined aria-hidden="true" className="mr-1.5 animate-spin motion-reduce:animate-none" />Calling...</> : "Try it"}
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    {embedded ? <div className="mb-3 sm:hidden">{apiSearchInput}</div> : apiSearchInput}

                    {filterCount > 0 && (
                        <div aria-label="Selected API filters" className="mb-3 flex flex-wrap gap-1.5">
                            {filterSections.flatMap(section => section.selected.map(option => (
                                <span className={`flex max-w-full items-center gap-1.5 rounded-md border py-1 pl-2.5 pr-1 text-xs transition-colors ${section.label === "Method" ? apiMethodFilterClassName(option) : "border-indigo-400/20 bg-indigo-500/10 text-indigo-200"}`} key={`${section.label}-${option}`}>
                                    {section.label !== "Method" && <span className="text-[10px] text-slate-500">{section.label}</span>}
                                    <span className="truncate font-semibold">{option}</span>
                                    <button aria-label={`Remove ${section.label.toLowerCase()} filter ${option}`} className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-current opacity-70 transition hover:bg-white/10 hover:opacity-100" onClick={() => section.setSelected(values => values.filter(value => value !== option))} type="button"><CloseOutlined className="text-[10px]" /></button>
                                </span>
                            )))}
                        </div>
                    )}
                    <div className={embedded ? "pr-1" : "-mr-3 max-h-[72vh] overflow-y-auto pr-3"}>
                        {isLoadingApis ? (
                            <div role="status" className="min-h-52 space-y-3 py-2">
                                <div className="flex items-center gap-2 pb-1 text-sm text-slate-400">
                                    <LoadingOutlined aria-hidden="true" className="animate-spin text-indigo-400 motion-reduce:animate-none" />
                                    <span>Loading APIs...</span>
                                </div>
                                <div aria-hidden="true" className="animate-pulse space-y-1.5 motion-reduce:animate-none">
                                    {["w-36", "w-52", "w-40", "w-48", "w-32"].map((width, index) => (
                                        <div className="flex h-12 items-center gap-2.5 border-b border-slate-700/60 px-1.5" key={width}>
                                            <span className={`h-8 w-20 shrink-0 rounded ${index % 2 ? "bg-emerald-500/15" : "bg-sky-500/15"}`} />
                                            <span className={`h-3 min-w-0 rounded bg-slate-700/60 ${width}`} />
                                            <span className="hidden h-2.5 w-28 rounded bg-slate-700/30 sm:block" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : apiListError ? (
                            <div className="flex min-h-52 flex-col items-center justify-center gap-3 text-center text-sm text-rose-400">
                                <span>{apiListError}</span>
                                <button
                                    className="rounded-md bg-indigo-500/15 px-3 py-2 font-semibold text-indigo-300 hover:bg-indigo-500/25"
                                    onClick={() => {
                                        setApiEndpoints([]);
                                        loadApis();
                                    }}
                                    type="button"
                                >
                                    Try again
                                </button>
                            </div>
                        ) : visibleApiEndpoints.length ? (
                            <div className="-mr-[11px] space-y-1.5">
                                {visibleApiEndpoints.map(endpoint => (
                                    <button
                                        className={`group relative block w-full rounded-none border border-transparent px-1.5 py-1.5 text-left transition-[background-color,border-radius] duration-300 ease-out hover:rounded-md motion-reduce:transition-none ${apiRowClassName(endpoint.method)}`}
                                        key={`${endpoint.method}-${endpoint.path}`}
                                        onClick={() => openApiForm(endpoint)}
                                        type="button"
                                    >
                                        <span aria-hidden="true" className="pointer-events-none absolute -inset-px rounded-[inherit]">
                                            <span className="api-row-outline absolute bottom-0 left-0 top-[6px] w-[7px] origin-bottom scale-y-0 rounded-bl-md border-l transition-transform duration-150 delay-300 ease-linear group-hover:scale-y-100 group-hover:delay-0 motion-reduce:transition-none" />
                                            <span className="api-row-outline absolute bottom-0 right-0 top-[6px] w-[7px] origin-bottom scale-y-0 rounded-br-md border-r transition-transform duration-150 delay-300 ease-linear group-hover:scale-y-100 group-hover:delay-0 motion-reduce:transition-none" />
                                            <span className="absolute left-0 top-0 h-[7px] w-1/2 [clip-path:inset(0_100%_0_0)] transition-[clip-path] duration-300 ease-linear group-hover:[clip-path:inset(0_0_0_0)] group-hover:delay-150 motion-reduce:transition-none">
                                                <span className="api-row-outline absolute inset-0 rounded-tl-md border-l border-t" />
                                            </span>
                                            <span className="absolute right-0 top-0 h-[7px] w-1/2 [clip-path:inset(0_0_0_100%)] transition-[clip-path] duration-300 ease-linear group-hover:[clip-path:inset(0_0_0_0)] group-hover:delay-150 motion-reduce:transition-none">
                                                <span className="api-row-outline absolute inset-0 rounded-tr-md border-r border-t" />
                                            </span>
                                        </span>
                                        <div className="flex min-w-0 items-center gap-2.5">
                                            <span className={`w-20 shrink-0 rounded px-2 py-1.5 text-center font-mono text-xs font-bold ${apiMethodClassName(endpoint.method)}`}>
                                                {endpoint.method}
                                            </span>
                                            <span className="min-w-0 break-all font-mono text-sm font-bold text-slate-200">{endpoint.path}</span>
                                            {endpoint.summary && <ApiSummary text={endpoint.summary} />}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <div className="flex min-h-52 items-center justify-center text-sm text-slate-500">No matching APIs.</div>
                        )}
                    </div>
                </>
            )}
        </>
    );

    if (embedded) return (
        <section className={`flex min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border border-[#373b44] bg-[#202329] sm:h-auto sm:min-h-[420px] sm:flex-1 ${selectedApiEndpoint ? "flex-none" : "flex-1"}`}>
            <div className="flex h-[42px] shrink-0 items-center gap-2 border-b border-[#373b44] px-2.5 text-slate-100 sm:h-[50px] sm:px-5">
                <ApiOutlined className="text-indigo-400" />
                <span className="font-semibold">APIs</span>
                <span className="shrink-0 text-xs text-slate-400">{endpointCount}</span>
                {apiAuthStatus === "authenticated" && !selectedApiEndpoint && <div className="mx-auto hidden min-w-0 max-w-md flex-1 sm:flex">{apiSearchInput}</div>}
                {apiAuthStatus === "authenticated" && <button className="group ml-auto gap-1 flex items-center shrink-0 rounded-md px-2 py-1 text-xs text-slate-400 transition-[color,background-color,transform] duration-200 ease-out hover:bg-rose-500/10 hover:text-rose-400 active:scale-95 motion-reduce:transform-none motion-reduce:transition-none" onClick={() => onLogout()} type="button"><LogoutOutlined className="transition-transform duration-200 ease-out group-hover:translate-x-0.5 motion-reduce:transform-none motion-reduce:transition-none" /> Logout</button>}
            </div>
            <div className={`min-h-0 overscroll-auto p-2.5 sm:flex-1 sm:overflow-auto sm:p-5 ${selectedApiEndpoint ? "flex-none overflow-visible" : "flex-1 overflow-auto"}`}>
                {content}
            </div>
        </section>
    );

    return (
        <Modal centered footer={null} onCancel={onClose} open={isOpen}
            rootClassName="[&_.ant-modal-container]:!bg-[#181a1f] [&_.ant-modal-close]:!text-slate-300 [&_.ant-modal-close:hover]:!bg-slate-700/70 [&_.ant-modal-close:hover]:!text-white"
            styles={{ body: { paddingTop: 16, paddingBottom: 5 }, close: { color: "#cbd5e1", top: 20, width: 28, height: 28 }, container: { background: "#181a1f", border: "1px solid #373b44", padding: 20 }, header: { background: "transparent", marginBottom: 0 } }}
            title={<div className="flex w-full items-center gap-2 pr-8 text-slate-100"><ApiOutlined className="text-indigo-400" /><span>{title || "APIs"}</span>{!title && apiAuthStatus === "authenticated" && <span className="rounded-full bg-slate-800 px-2 py-0.5 text-xs font-normal text-slate-400">{endpointCount}</span>}{apiAuthStatus === "authenticated" && <button className="group ml-auto flex h-7 items-center gap-1.5 rounded-md px-2 text-xs font-semibold text-slate-400 transition-[color,background-color,transform] duration-200 ease-out hover:bg-rose-500/10 hover:text-rose-400 active:scale-95 motion-reduce:transform-none motion-reduce:transition-none" onClick={() => onLogout()} type="button"><LogoutOutlined className="transition-transform duration-200 ease-out group-hover:translate-x-0.5 motion-reduce:transform-none motion-reduce:transition-none" />Logout</button>}</div>}
            width={900}>
            {content}
        </Modal>
    );
};

export default ApiModal;
